import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Receipt, Trash2 } from "lucide-react";
import { http } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import DashShell from "@/components/dash/DashShell";
import { Card, StatusPill } from "@/components/portal/ui";
import { money } from "@/lib/constants";
import NotificationBell from "@/components/dash/NotificationBell";
import { OP_LINKS } from "./OperatorDashboard";

const NAMES = { diagnostic: "Clarity", blueprint: "Personalized Blueprint", guided: "Guided Virtual Consulting", guided_standalone: "Guided Implementation Support", inhome_overnight: "In-Home + Overnight" };

export default function OperatorOrders() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [product, setProduct] = useState("all");
  const [pay, setPay] = useState("all");
  useSeo({ title: "Orders", noindex: true });
  useEffect(() => { http.get("/operator/orders").then((r) => setD(r.data)).catch(() => setD({ orders: [], revenue: 0 })); }, []);
  const reload = () => http.get("/operator/orders").then((r) => setD(r.data)).catch(() => {});
  const delTest = async (e, o) => {
    e.stopPropagation();
    if (!window.confirm(`Delete test order ${o.order_no}? This cannot be undone.`)) return;
    try { await http.delete(`/admin/test-order/${o.id}`); toast.success("Test order deleted"); reload(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not delete"); }
  };

  if (!d) return <DashShell brandLabel="Consultant Workspace" links={OP_LINKS("orders")} rightSlot={<NotificationBell />}><p className="text-[#6B665E]">Loading…</p></DashShell>;
  const orders = d.orders.filter((o) => (product === "all" || o.product_key === product) && (pay === "all" || o.payment_status === pay));

  return (
    <DashShell brandLabel="Consultant Workspace" links={OP_LINKS("orders")} rightSlot={<NotificationBell />}>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="eyebrow text-[#8A998C] mb-1">Transactions</div>
          <h1 className="font-display text-3xl text-[#1A1918]">Orders</h1>
        </div>
        <Card className="px-5 py-3" data-testid="orders-revenue"><div className="eyebrow text-[#8A998C] mb-0.5">Net revenue</div><div className="font-display text-2xl text-[#4d6b4d]">{money(d.revenue)}</div></Card>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        <select data-testid="filter-product" value={product} onChange={(e) => setProduct(e.target.value)} className="text-sm rounded-lg border border-[#E5E1D8] bg-white px-3 py-2 text-[#1A1918]">
          <option value="all">All products</option>
          {Object.entries(NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select data-testid="filter-payment" value={pay} onChange={(e) => setPay(e.target.value)} className="text-sm rounded-lg border border-[#E5E1D8] bg-white px-3 py-2 text-[#1A1918]">
          <option value="all">All payments</option>
          <option value="paid">Paid</option>
          <option value="refunded">Refunded</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {orders.length === 0 ? (
        <Card className="p-10 text-center" data-testid="orders-empty">
          <Receipt size={24} className="text-[#C9C3B8] mx-auto mb-3" />
          <h3 className="font-display text-lg text-[#1A1918] mb-1">No orders yet</h3>
          <p className="text-[#6B665E] text-sm">Successful purchases will appear here automatically.</p>
        </Card>
      ) : (
        <Card className="p-0 overflow-x-auto" data-testid="orders-table">
          <table className="w-full text-sm min-w-[820px]">
            <thead><tr className="text-left text-[#8A998C] border-b border-[#F0ECE4] text-xs uppercase tracking-wide">
              {["Order", "Customer", "Product", "Amount", "Payment", "Date", "Client status", ""].map((h, i) => <th key={i} className="p-4 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.id}`} onClick={() => o.customer_id && nav(`/operator/customer/${o.customer_id}`)}
                  className={`border-b border-[#F7F4EE] last:border-0 ${o.customer_id ? "hover:bg-[#FAF8F3] cursor-pointer" : ""}`}>
                  <td className="p-4 text-[#1A1918] font-mono text-xs">{o.order_no}</td>
                  <td className="p-4 text-[#1A1918]">{o.customer}{o.is_test && <span className="ml-2 text-[9px] uppercase bg-[#C49A5A]/20 text-[#8a6a2e] rounded-full px-1.5 py-0.5">Test</span>}<div className="text-[#8A998C] text-xs">{o.email}</div></td>
                  <td className="p-4 text-[#6B665E]">{NAMES[o.product_key] || o.product_key || "—"}</td>
                  <td className="p-4 text-[#1A1918]">{money(o.amount)}</td>
                  <td className="p-4"><span className={`text-xs px-2 py-0.5 rounded-full ${o.payment_status === "paid" ? "bg-[#667A66]/12 text-[#4d6b4d]" : o.payment_status === "refunded" ? "bg-[#C49A5A]/15 text-[#8a6a2e]" : "bg-[#9E4F4F]/12 text-[#9E4F4F]"}`}>{o.payment_status}</span></td>
                  <td className="p-4 text-[#8A998C]">{o.date ? new Date(o.date).toLocaleDateString() : "—"}</td>
                  <td className="p-4">{o.client_status ? <StatusPill state={o.client_status} /> : <span className="text-[#8A998C] text-xs">—</span>}</td>
                  <td className="p-4">{o.is_test && (
                    <button onClick={(e) => delTest(e, o)} data-testid={`order-delete-${o.id}`} title="Delete test order" className="inline-flex items-center gap-1 text-xs text-[#9E4F4F] hover:underline"><Trash2 size={13} /> Delete</button>
                  )}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </DashShell>
  );
}
